import {
  PostPrayerStreamErrorResponseDtoStatus,
  PostPrayerStreamSuccessResponseDtoStatus,
  ResponseMetadataMessageDtoLevel,
} from '@ccm-innovation/member-center-bff-sdk-axios';
import dayjs from 'dayjs';
import { assert, UnreachableCaseError } from 'ts-essentials';
import { call, put, select, takeLeading } from 'typed-redux-saga';
import { PrayerStreamApiService } from '../../../services/prayer-stream';
import { getAccessTokenSaga } from '../../auth/accessToken.saga';
import { type RootState } from '../../root.state';
import { PrayerStreamUserStatus, streamSlice } from '../../stream/stream.slice';
import { getContainer } from '../../utils.saga';
import { registerPrayerStreamUserSlice } from './register-prayer-stream-user.slice';

export function* registerPrayerStreamUserSaga(): Generator {
  const container = yield* getContainer();
  const { prayerStreamApi } = container.get(PrayerStreamApiService);

  const { contactUid, name } = yield* select((state: RootState) => state.auth);
  assert(contactUid, 'registerPrayerStreamUserSaga: contactUid');
  assert(name, 'registerPrayerStreamUserSaga: name');

  yield* put(
    registerPrayerStreamUserSlice.actions.registerPrayerStreamUserStarted(),
  );

  const accessToken = yield* getAccessTokenSaga();
  assert(accessToken);

  // TODO: Create wrapper function to handle Axios errors.
  const { data } = yield* call(async () => {
    try {
      return await prayerStreamApi.prayerStreamControllerCreateMemberStatusV1(
        {
          postPrayerStreamRequestDto: {
            acceptedAgreement: dayjs().toISOString(),
            name,
          },
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
    } catch (error) {
      return {
        data: {
          metadata: {
            messages: [
              {
                level: ResponseMetadataMessageDtoLevel.ERROR,
                text: `PrayerStream level error: ${(error as Error).message}`,
              },
            ],
            requestId: '001',
          },
          status: PostPrayerStreamErrorResponseDtoStatus.OTHER,
        },
      };
    }
  });

  switch (data.status) {
    case PostPrayerStreamErrorResponseDtoStatus.ACCESS_DENIED:
    case PostPrayerStreamErrorResponseDtoStatus.OTHER:
      yield* put(
        registerPrayerStreamUserSlice.actions.registerPrayerStreamUserFailed(
          data,
        ),
      );
      break;
    case PostPrayerStreamSuccessResponseDtoStatus.SUCCESS: {
      yield* put(
        registerPrayerStreamUserSlice.actions.registerPrayerStreamUserSucceeded(),
      );

      const { communityChannelData, userData } = data.payload;

      yield* put(streamSlice.actions.setPrayerStreamUserDetails(userData));
      yield* put(
        streamSlice.actions.setCommunityGroupList(communityChannelData),
      );
      yield* put(
        streamSlice.actions.setPrayerStreamUserStatus(
          PrayerStreamUserStatus.SELECT_COMMUNITY_GROUPS,
        ),
      );
      break;
    }
    /* v8 ignore next 2 */
    default:
      throw new UnreachableCaseError(data);
  }
}
export function* watchRegisterPrayerStreamUserRequestsSaga(): Generator {
  yield* takeLeading(
    registerPrayerStreamUserSlice.actions.registerPrayerStreamUserRequested,
    function* lambda(action) {
      yield* call(registerPrayerStreamUserSaga, action.payload);
    },
  );
}
